/** @forge-header
 * @id          bookSides
 * @role        Display-side ladder builder | phantom-level filter + YES/NO mirror
 * @pattern     pure
 * @purity      pure
 * @units       cents
 * @money-path  no
 * @upstream    pricing/subCent:mirrorCents | ValueDashboard/types:BookLevel,VenueBook,MarketSide
 * @downstream  ValueDashboard/GameDetailPanel | Cockpit/live/LiveBooks
 * @exports     buildSide:fn | bidIsReal:fn | mirrorCents:fn | LEVEL_SANITY_SLACK:const
 */

// Hoisted verbatim out of GameDetailPanel (2026-08-21) so the Cockpit's live
// books render the SAME sane ladder as the ticket — same phantom-level filter
// the inline ladder + Yes/No pills use, so the deep orderbook never diverges
// on tiny stub orders / cross-spread ghosts. Zero behavior change.
import { mirrorCents } from '@/lib/pricing/subCent';
import type { BookLevel, MarketSide, VenueBook } from '@/views/ValueDashboard/types';

export { mirrorCents };

export const LEVEL_SANITY_SLACK = 15;

function hasSize(l: BookLevel): boolean {
  return l.size >= 1;
}

/** A bid level is "real" when it carries ≥1 contract AND sits at/below the
 *  last trade plus a sanity slack. Drops FP dust + cross-spread ghosts. */
export function bidIsReal(lastTradeCents: number | null | undefined) {
  return (l: BookLevel): boolean => {
    if (!hasSize(l)) return false;
    if (lastTradeCents == null) return true;
    return l.priceCents <= lastTradeCents + LEVEL_SANITY_SLACK;
  };
}

/** Own-side bids + mirrored opposite-side bids as asks, filtered to real levels. */
export function buildSide(
  book: VenueBook | null | undefined,
  marketSide: MarketSide,
): { bids: BookLevel[]; asks: BookLevel[] } {
  if (!book) return { bids: [], asks: [] };
  const ownBids = marketSide === 'yes' ? book.yes : book.no;
  const oppositeBids = marketSide === 'yes' ? book.no : book.yes;
  const lastYes = book.lastPriceCents;
  const ownLast = lastYes == null ? null : marketSide === 'yes' ? lastYes : mirrorCents(lastYes);
  const oppositeLast = lastYes == null ? null : marketSide === 'yes' ? mirrorCents(lastYes) : lastYes;
  return {
    bids: ownBids.filter(bidIsReal(ownLast)),
    asks: oppositeBids
      .filter(bidIsReal(oppositeLast))
      // Exact mirror — sub-cent ask levels must land on the grid (99.9¢ bid
      // → exactly 0.1¢ ask), or strict price matches downstream miss them.
      .map((l) => ({ priceCents: mirrorCents(l.priceCents), size: l.size })),
  };
}
